import * as React from 'react';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Backdrop, CircularProgress, Pagination, Tooltip } from '@mui/material';
import HttpService from '../../hooks/Https-services';
import { API_ROUTES, dateFormat } from '../../utils/constant';
import moment from 'moment';
import { getCurrentAuth } from '../../hooks/http-server';
export const formatDuration = (elapsedTimes: any) => {
  const days = Math.floor(elapsedTimes / (3600 * 24));
  const hours = Math.floor((elapsedTimes % (3600 * 24)) / 3600);
  const minutes = Math.floor((elapsedTimes % 3600) / 60);
  const seconds = Math.floor(elapsedTimes % 60);

  const daysStr = days > 0 ? `${days} Days ` : '';
  const hoursStr = hours.toString().padStart(2, '0');
  const minutesStr = minutes.toString().padStart(2, '0');
  const secondsStr = seconds.toString().padStart(2, '0');

  return `${daysStr}${hoursStr}h:${minutesStr}m:${secondsStr}s`;
};
function Row(props: { row: any }) {
  const { row } = props;
  const [open, setOpen] = React.useState(false);

  const totalBreakDuration =
    row.breaks?.reduce(
      (acc: any, logsRow: any) => acc + logsRow.totalDuration,
      0
    ) || 0;

  const totalTimeSpent = row.totalDuration - totalBreakDuration;

  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row" align="left">
          {moment(row?.date).format(`${dateFormat}`)}
        </TableCell>
        <TableCell align="left">
          {moment(row?.checkInTime).format('hh:mm:ss a')}
        </TableCell>
        <TableCell align="left">
          {row?.checkOutTime != null
            ? moment(row?.checkOutTime).format('hh:mm:ss a')
            : `- - -`}
        </TableCell>
        <TableCell align="left">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {' '}
            {row?.checkOutTime != null
              ? formatDuration(totalTimeSpent)
              : `- - -`}
            {row?.is_missing_punch && (
              <Tooltip title="Checkout Was Missing">
                <Box
                  sx={{
                    height: '15px',
                    width: '15px',
                    borderRadius: '15px',
                    margin: '0 10px',
                    backgroundColor: 'red',
                  }}
                ></Box>
              </Tooltip>
            )}
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom component="div">
                Breaks
              </Typography>
              <Table size="small" aria-label="purchases">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Break Start</TableCell>
                    <TableCell align="right">Break End</TableCell>
                    <TableCell align="right">Total Duration</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.breaks?.length > 0 ? (
                    row.breaks.map((logsRow: any) => (
                      <TableRow key={logsRow.id}>
                        <TableCell component="th" scope="row">
                          {moment(logsRow?.date).format(`${dateFormat}`)}
                        </TableCell>
                        <TableCell>
                          {moment(logsRow?.breakStart).format('hh:mm:ss a')}
                        </TableCell>
                        <TableCell align="right">
                          {logsRow?.breakEnd != null
                            ? moment(logsRow?.breakEnd).format('hh:mm:ss a')
                            : `- - -`}
                        </TableCell>
                        <TableCell align="right">
                          {logsRow?.breakEnd != null
                            ? formatDuration(logsRow.totalDuration)
                            : `- - -`}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4}>
                        {' '}
                        {/* Adjust colSpan based on the number of columns */}
                        <Typography>No Data</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

export default function ClockInOutLog({ id }: { id?: any }) {
  const [logs, setLogs] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(10);
  const [currentId, setCurrentId] = React.useState<number | undefined>(id);
  const itemsPerPage = 10;
  const auth = getCurrentAuth();
  const fetchLogList = React.useCallback(
    async (id?: number, page = currentPage) => {
      try {
        setLoading(true);

        if (id) {
          setCurrentId(id); // Update the stored ID
        }

        const query: any = {
          page: page,
          limit: itemsPerPage,
        };

        const apiUrl = currentId
          ? `${API_ROUTES.clockInOutLogsById}/${currentId}`
          : API_ROUTES.clockInOutLogs;

        const response = await HttpService.get(
          `${apiUrl}?${new URLSearchParams(query).toString()}`
        );
        setLogs(response.data.data.list);
        setTotalPages(response.data.data.totalPages);
      } catch (error: any) {
        console.error('Error fetching logs list:', error);
      } finally {
        setLoading(false);
      }
    },
    [currentId, currentPage, itemsPerPage]
  );

  React.useEffect(() => {
    if (auth.Token) {
      fetchLogList(currentId); // Ensure the currentId is used
    }
  }, [fetchLogList]);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setCurrentPage(page);
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell align="left">Date</TableCell>
              <TableCell align="left">Check In</TableCell>
              <TableCell align="left">Check Out</TableCell>
              <TableCell align="left">Total Duration</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(logs) && logs.length > 0 ? (
              logs.map((row: any) => <Row key={row.id} row={row} />)
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  {' '}
                  {/* Adjust colSpan based on total columns */}
                  <Typography>No Data</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Pagination Component */}
      <Box display="flex" justifyContent="center" my={2}>
        <Pagination
          count={totalPages} // Total number of pages
          page={currentPage} // Current page
          onChange={handlePageChange} // Handle page change
          color="primary"
        />
      </Box>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}
